/**
 * useAchievementsData - Manages achievements/certificates data
 *
 * This hook manages student achievements and certificate requests.
 * Used by AchievementsContainer.
 *
 * Follows the URL-as-source-of-truth pattern established by useTermsData:
 * - Auto-fetches when urlGradeCode changes
 * - Exposes requestCertificate for user-initiated actions
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { achievementsApi } from "@/api/achievementsApi";
import { REQUEST_STATUS } from "@ts/enums/RequestStatus";
import type {
  AchievementApiItem,
  CertificateRequestParams,
  CertificateRequestResponse
} from "@ts/interfaces/achievements/api/achievementsApiInterface";
import type { ErrorInterface } from "@ts/interfaces/ApiResponseInterface";

interface UseAchievementsDataParams {
  urlGradeCode: number | undefined;
}

export interface AchievementsDataState {
  // Data
  achievements: AchievementApiItem[];

  // Status
  achievementsStatus: REQUEST_STATUS;
  achievementsError: ErrorInterface | null;
}

export interface AchievementsDataActions {
  requestCertificate: (
    params: CertificateRequestParams
  ) => Promise<CertificateRequestResponse>;
  invalidateAchievementsCache: () => void;
}

export type AchievementsData = AchievementsDataState & AchievementsDataActions;

export function useAchievementsData({
  urlGradeCode
}: UseAchievementsDataParams): AchievementsData {
  const [achievements, setAchievements] = useState<AchievementApiItem[]>([]);
  const [achievementsStatus, setAchievementsStatus] = useState<REQUEST_STATUS>(
    REQUEST_STATUS.IDLE
  );
  const [achievementsError, setAchievementsError] =
    useState<ErrorInterface | null>(null);

  const lastFetchedGradeRef = useRef<number | undefined>(undefined);

  // Invalidate cache so next render cycle will refetch
  const invalidateAchievementsCache = useCallback(() => {
    lastFetchedGradeRef.current = undefined;
  }, []);

  // Auto-fetch when the target grade changes or the cache is invalidated.
  useEffect(() => {
    if (!urlGradeCode) {
      setAchievements([]);
      setAchievementsStatus(REQUEST_STATUS.IDLE);
      setAchievementsError(null);
      lastFetchedGradeRef.current = undefined;
      return;
    }

    // Already fetched for this grade during the current cache window.
    if (lastFetchedGradeRef.current === urlGradeCode) {
      return;
    }

    lastFetchedGradeRef.current = urlGradeCode;
    setAchievementsStatus(REQUEST_STATUS.PENDING);
    setAchievementsError(null);

    achievementsApi
      .getAchievements({ grade: urlGradeCode })
      .then(response => {
        // Guard against stale responses when grade changed mid-flight
        if (lastFetchedGradeRef.current !== urlGradeCode) return;
        setAchievements(response ?? []);
        setAchievementsStatus(REQUEST_STATUS.SUCCESS);
      })
      .catch((error: unknown) => {
        // Guard against stale responses when grade changed mid-flight
        if (lastFetchedGradeRef.current !== urlGradeCode) return;
        console.error("Error fetching achievements:", error);
        setAchievementsStatus(REQUEST_STATUS.ERROR);
        lastFetchedGradeRef.current = undefined;

        const err = error as {
          customAttributes?: { httpErrorStatus?: number };
          message?: string;
        };
        setAchievementsError({
          code: err?.customAttributes?.httpErrorStatus ?? 500,
          message: err?.message ?? "Failed to fetch achievements"
        });
      });
  }, [urlGradeCode]);

  const requestCertificate = useCallback(
    async (
      params: CertificateRequestParams
    ): Promise<CertificateRequestResponse> => {
      try {
        const response = await achievementsApi.requestCertificate(params);
        return response;
      } catch (error: unknown) {
        console.error("Error requesting certificate:", error);
        const err = error as {
          customAttributes?: { httpErrorStatus?: number };
          message?: string;
        };
        throw {
          code: err?.customAttributes?.httpErrorStatus,
          message: err?.message ?? "Failed to request certificate"
        };
      }
    },
    []
  );

  return {
    // State
    achievements,
    achievementsStatus,
    achievementsError,

    // Actions
    requestCertificate,
    invalidateAchievementsCache
  };
}
